import { createSlice } from '@reduxjs/toolkit';

const initialState = {
  systemLogs: [],
  loading: false,
  error: null,
  // Init data
  types: {},
  actions: {},
  categories: {},
  initLoading: false,
  // Pagination
  page: 1,
  pageSize: 10,
  total: 0,
  // Sorting
  sortColumn: 'createdOn',
  sortDirection: 'desc',
  // Filtering
  filterTitle: '',
  filterTypes: [], // Array of selected type IDs
  filterActions: [], // Array of selected action IDs
  filterCategories: [], // Array of selected category keys
  filterAccount: '', // Account (username) filter
  filterFromDate: '', // Date range filter - from date
  filterToDate: '', // Date range filter - to date
};

const systemLogsSlice = createSlice({
  name: 'systemLogs',
  initialState,
  reducers: {
    // Fetch init data
    fetchInit: (state) => {
      state.initLoading = true;
      state.error = null;
    },
    fetchInitSuccess: (state, action) => {
      state.initLoading = false;
      state.types = action.payload.types;
      state.actions = action.payload.actions;
      state.categories = action.payload.categories || {};
      state.error = null;
    },
    fetchInitFailure: (state, action) => {
      state.initLoading = false;
      state.error = action.payload;
    },

    // Fetch system logs
    fetchSystemLogs: (state) => {
      state.loading = true;
      state.error = null;
    },
    fetchSystemLogsSuccess: (state, action) => {
      state.loading = false;
      state.systemLogs = action.payload.data;
      state.total = action.payload.meta.total;
      state.error = null;
    },
    fetchSystemLogsFailure: (state, action) => {
      state.loading = false;
      state.error = action.payload;
    },

    // Pagination
    setPage: (state, action) => {
      state.page = action.payload;
    },
    setPageSize: (state, action) => {
      state.pageSize = action.payload;
      state.page = 1; // Reset to first page
    },

    // Sorting
    setSortColumn: (state, action) => {
      state.sortColumn = action.payload;
    },
    setSortDirection: (state, action) => {
      state.sortDirection = action.payload;
    },

    // Filtering
    setFilterTitle: (state, action) => {
      state.filterTitle = action.payload;
      state.page = 1; // Reset to first page when filtering
    },
    setFilterTypes: (state, action) => {
      state.filterTypes = action.payload;
      state.page = 1;
    },
    setFilterActions: (state, action) => {
      state.filterActions = action.payload;
      state.page = 1;
    },
    setFilterCategories: (state, action) => {
      state.filterCategories = action.payload;
      state.page = 1;
    },
    setFilterAccount: (state, action) => {
      state.filterAccount = action.payload;
      state.page = 1; // Reset to first page when filtering
    },
    setFilterFromDate: (state, action) => {
      state.filterFromDate = action.payload;
      state.page = 1; // Reset to first page when filtering
    },
    setFilterToDate: (state, action) => {
      state.filterToDate = action.payload;
      state.page = 1; // Reset to first page when filtering
    },

    // Clear error
    clearError: (state) => {
      state.error = null;
    },
  },
});

export const {
  fetchInit,
  fetchInitSuccess,
  fetchInitFailure,
  fetchSystemLogs,
  fetchSystemLogsSuccess,
  fetchSystemLogsFailure,
  setPage,
  setPageSize,
  setSortColumn,
  setSortDirection,
  setFilterTitle,
  setFilterTypes,
  setFilterActions,
  setFilterCategories,
  setFilterAccount,
  setFilterFromDate,
  setFilterToDate,
  clearError,
} = systemLogsSlice.actions;

// Selectors
export const selectSystemLogs = (state) => state.systemLogs?.systemLogs || [];
export const selectSystemLogsLoading = (state) => state.systemLogs?.loading || false;
export const selectSystemLogsError = (state) => state.systemLogs?.error;
export const selectSystemLogsTotal = (state) => state.systemLogs?.total || 0;
export const selectSystemLogsPage = (state) => state.systemLogs?.page || 1;
export const selectSystemLogsPageSize = (state) => state.systemLogs?.pageSize || 10;
export const selectSystemLogsSortColumn = (state) => state.systemLogs?.sortColumn || 'createdOn';
export const selectSystemLogsSortDirection = (state) => state.systemLogs?.sortDirection || 'desc';
export const selectSystemLogsFilterTitle = (state) => state.systemLogs?.filterTitle || '';
export const selectSystemLogsFilterTypes = (state) => state.systemLogs?.filterTypes || [];
export const selectSystemLogsFilterActions = (state) => state.systemLogs?.filterActions || [];
export const selectSystemLogsFilterCategories = (state) => state.systemLogs?.filterCategories || [];
export const selectSystemLogsFilterAccount = (state) => state.systemLogs?.filterAccount || '';
export const selectSystemLogsFilterFromDate = (state) => state.systemLogs?.filterFromDate || '';
export const selectSystemLogsFilterToDate = (state) => state.systemLogs?.filterToDate || '';
export const selectSystemLogsTypes = (state) => state.systemLogs?.types || {};
export const selectSystemLogsActions = (state) => state.systemLogs?.actions || {};
export const selectSystemLogsCategories = (state) => state.systemLogs?.categories || {};
export const selectSystemLogsInitLoading = (state) => state.systemLogs?.initLoading || false;

export default systemLogsSlice.reducer;
